// Brief 85 §4 — TaskModal, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN — AT EXTRACTION, WHICH IS THE ONLY THING THE HASH BELOW
// CAN SAY. The statement block was byte-identical to the body of the tk clause
// as it stood in App.tsx —
//   sha256 cfa9676f3f09279bf70bb5db4f4d9c2a16fc668f158dba7e15eee34b0c04eeb4
// — indentation included, which is why it still sits at the depth it had inside
// App(). Only the imports, the Props type and the signature were new.
//
// IT IS NO LONGER THAT BLOCK, and this line is here because the paragraph above
// went on claiming it was. 86 Phase 2 step 9 replaced three of its regions with
// shared components; brief 90 §5 merged the Description card; brief 91 §1a took
// five props off the subtask checklist below. The hash records the extraction.
// It is not a claim about this file, and re-hashing it would prove nothing —
// there is nothing left in App.tsx to compare against. (RULINGS 2026-08-18: a
// file asserting a state that is not true is the recurring defect, and no gate
// catches it.)
//
// Do not reformat: the indentation is still the extraction's own.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. The dialog declares no hook (§0
// measured zero across all 21 blocks), so it moves as a component without any
// state ownership changing hands.

import AssigneePicker from "../../components/AssigneePicker";
import StageLinkField from "../StageLinkField";
import MatterPicker, { formMatterId, formMatterKind } from "../MatterPicker"; // 131 §3; 184 A.1 — the two helpers
import type { TaxEntry, Jur } from "../StageLinkField";
import SubtaskChecklist from "../../components/SubtaskChecklist";
import TemplateSubtasksButton from "./TemplateSubtasksButton"; // 86 Phase 2 step 9
import ReminderLevelFields from "./ReminderLevelFields"; // 86 Phase 2 step 9
import TaskDocsPane from "../../components/TaskDocsPane";
import { EISEN_QUADRANTS, TASK_STATUSES, eisenAuto } from "../../utils";
import { EisenBadge, eisenLabel } from "../../EisenBadge";
import { FS, RAD, Z } from "../../lib/tokens";
import { ModalLayout } from "../../components/SharedUI";
import LinkChainFooter from "../LinkChainFooter"; // 111 §5b
import { X } from "lucide-react";
import { _buildTplItems, _reconcileTpl } from "../../lib/templatePreview";
import { isDividerCode } from "../../utils/taxonomyMap"; // 110 §3a
import type { TFn, TaxList } from "../../lib/taxonomyTypes";
import FieldSelect from "../FieldSelect"; // 154B §2
import type { FieldSelectOption } from "../FieldSelect";
import type { makeModalFields } from "../modalFields";

type Vocab = ReturnType<typeof makeModalFields>;

type Props = {
  F: Vocab["F"];
  S: Vocab["S"];
  T: Vocab["T"];
  _fl: Vocab["_fl"];
  _mt: Vocab["_mt"];
  _appIsAdmin: boolean;
  _appPerms: any;
  actEntries: TaxList;
  // 109 §7e — the case_stage entries, for stageAutofill's NAME resolve.
  caseStageEntries: TaxList;
  askSubtaskDone: (task: unknown, subtask: unknown) => void;
  cases: any[];
  cm: () => void;
  collaborators: any[];
  collectiveCases: any[];
  currentUser: any;
  // BRIEF 87 §8 — for the stage picker's jurisdiction-name autofill.
  jurisdictions?: readonly Jur[];
  eisenPickerOpen: any;
  form: any;
  // 131 §3 — the picker's recents arm, the same reader the te/ag modals take.
  getRecentCases: (userId: unknown) => readonly unknown[];
  holdEntityTimer: (taskId: unknown, subtaskId: unknown) => { taskId: unknown; subtaskId: unknown; wasRunning: boolean; elapsed: number } | null;
  itemLevelEntries: TaxList;
  openModal: (t: unknown, e?: unknown) => void;
  reminderOptsList: readonly FieldSelectOption[];
  saveTask: (fd2: unknown) => boolean;
  /* 111 §5b — the link chain: chainTask(null) is the plain link-aware save
     (saveTask + links + cm); chainTask(kind) spawns the next modal. */
  chainTask: (next: "stage" | "task" | "event" | null) => boolean;
  linkState: {
    unlinkDisabled: boolean;
    spawnDisabled: boolean;
    onUnlinkAll: () => void;
    /* 112 §7 — the Links manager's door. */
    linkDisabled: boolean;
    onLinkManager: () => void;
  };
  setEisenPickerOpen: (v: unknown) => void;
  setForm: (v: unknown) => void;
  setTasks: (v: unknown) => void;
  setTkCompletePend: (v: unknown) => void;
  t: TFn;
  taskStatusesList: readonly FieldSelectOption[];
  tasks: any[];
  tkTypes: any[];
  toast: (m?: string, type?: string, opts?: { action?: unknown }) => void;
  updateField: (k: string, v: unknown) => void;
};

export default function TaskModal({
  F,
  S,
  T,
  _fl,
  _mt,
  _appIsAdmin,
  _appPerms,
  actEntries,
  caseStageEntries,
  askSubtaskDone,
  cases,
  cm,
  collaborators,
  collectiveCases,
  currentUser,
  jurisdictions,
  eisenPickerOpen,
  form,
  getRecentCases,
  holdEntityTimer,
  itemLevelEntries,
  openModal,
  reminderOptsList,
  saveTask,
  chainTask,
  linkState,
  setEisenPickerOpen,
  setForm,
  setTasks,
  setTkCompletePend,
  t,
  taskStatusesList,
  tasks,
  tkTypes,
  toast,
  updateField,
}: Props) {
  /* 111 §5b — the save body, once, for the Mod's onS (Enter) AND the chain
     footer's Save. The completion-pend flow BEATS a chain click (enumerated:
     the prompt path owns the modal's close and the te-offer; a spawn during
     a completing edit falls back to the plain flow). */
  const _completionPend = () =>
    form.id &&
    form.status === "completed" &&
    tasks.find((t: any) => t.id === form.id)?.status !== "completed";
  const _pendFlow = () => {
    cm();
    /* Round 15 (C91) — ccId normalized alongside caseId (a cc
       task completed through the modal used to pend a string —
       the prompt's te payload then mis-bound), and the stored
       row backfills anything the form copy lacks (row-attached
       case-title hints included). */
    setTkCompletePend({
      ...(tasks.find((t2: any) => t2.id === form.id) || {}),
      ...form,
      caseId: form.caseId ? +form.caseId : null,
      ccId: form.ccId ? +form.ccId : null,
      assignedCoId: form.assignedCoId ? +form.assignedCoId : null,
      priority: form.priority || null,
      /* Round 17 (C102) — same hold as the pill flow. */
      _timerHold: holdEntityTimer(form.id, null),
    });
  };
  /* 184 A.1 — the form's matter ids, read ONCE and typed for the picker:
     `""` (a cleared field) and null both mean none. */
  const _caseIdF = formMatterId(form.caseId);
  const _ccIdF = formMatterId(form.ccId);
  const _doSave = () => {
    if (!form.title) {
      toast(
        t("toast.title_required", {
          fallback: "⚠ Title is required",
        }),
      );
      return;
    }
    if (_completionPend()) {
      _pendFlow();
      return;
    }
    /* chainTask(null) = saveTask + pairwise links (when chained) + cm. */
    if (chainTask(null)) {
      toast(
        form.id
          ? t("toast.updated", { fallback: "Updated" })
          : t("toast.task_added", { fallback: "Task added" }),
      );
    }
  };
  const _doSpawn = (k: "stage" | "task" | "event") => {
    if (_completionPend()) {
      _doSave();
      return;
    }
    chainTask(k);
  };
  return (
          <ModalLayout
            title={form.id ? _mt("Edit Task") : _mt("New Task")}
            onClose={cm}
            onSave={_doSave}
            footer={
              <LinkChainFooter
                kind="task"
                onSave={_doSave}
                onSpawn={_doSpawn}
                spawnDisabled={linkState.spawnDisabled}
                unlinkDisabled={linkState.unlinkDisabled}
                onUnlinkAll={linkState.onUnlinkAll}
                linkDisabled={linkState.linkDisabled}
                onLinkManager={linkState.onLinkManager}
                onCancel={cm}
              />
            }
          >
              <>
                {/* 184 A.1 [RULED] — THE FIELD SHOWS INSIDE A CASE TOO, prefilled
                    with the current matter; changing it is a MOVE the server
                    guards (tasks.js PUT: the target right, the stage link, the
                    item links, the time entries). The `!form._caseFixed &&`
                    guard that hid it for every in-case caller is gone; the
                    key's writers stay and are inert on this path (they live on
                    for the time-entry modal). */}
                <div className="field">
                  <label>{t("field.case", { fallback: "Case" })}</label>
                  {/* 131 §3 — the plain <select> over `cases` could not
                      represent a collective case AT ALL, and everything
                      downstream already handles one: `:152` normalises both
                      ids on save, `:544`/`:709` resolve a cc, and the Task
                      model carries `caseId` and `ccId` side by side. The
                      archived filter this select carried survives as
                      `excludeArchived`, and the empty option survives as the
                      clear row — a task may legitimately have no matter.
                      184 A.1 — `selectedKind` (132 §1): now that the field
                      renders inside every collective case, a cc id must not
                      resolve to the case that happens to share its number. */}
                  <MatterPicker
                    t={t}
                    cases={cases}
                    collectiveCases={collectiveCases}
                    selectedId={_caseIdF ?? _ccIdF}
                    selectedKind={formMatterKind(_caseIdF, _ccIdF)}
                    placeholder={t("modal.matter.search_ph", {
                      fallback: "Search case…",
                    })}
                    getRecentCases={getRecentCases}
                    currentUser={currentUser}
                    excludeArchived
                    sortByTitle
                    clearLabel={t("modal.tk.no_case", {
                      fallback: "— No case —",
                    })}
                    onClear={() =>
                      setForm((p: any) => ({ ...p, caseId: "", ccId: "" }))
                    }
                    onPick={(m) =>
                      setForm((p: any) => ({
                        ...p,
                        caseId: m._type === "ca" ? m.id : "",
                        ccId: m._type === "cc" ? m.id : "",
                      }))
                    }
                  />
                </div>
                {/* 154B §2 — FieldSelect's own shape. The 110 §3a divider
                    branch becomes 108 §6b's row, which is the same rule drawn
                    by the menu rather than by a string inside a disabled
                    <option>; the option map's parameter is renamed off `t`,
                    which in this file is the translator. */}
                <FieldSelect
                  label={_fl("Type")}
                  value={form.type || ""}
                  onChange={(v) => {
                    setForm((p: any) => {
                      if (p.id) return { ...p, type: v };
                      const on = p.templatesOn !== false;
                      const tpl = on
                        ? _buildTplItems(actEntries, v, p.dueDate)
                        : [];
                      return {
                        ...p,
                        type: v,
                        subtasks: _reconcileTpl(p.subtasks, tpl, on),
                      };
                    });
                  }}
                  options={[
                    ["", t("common.select_dashed", { fallback: "— Select —" })] as const,
                    ...tkTypes.map(
                      (ty: any) =>
                        [
                          String(ty.code),
                          isDividerCode(ty.code)
                            ? String(ty.desc || "")
                            : `${ty.code} — ${ty.desc}`,
                        ] as const,
                    ),
                  ]}
                />
                {/* BRIEF 87 §8 — full width, immediately after Type, the
                    SAME component the Event modal mounts. §10(iii)'s "the
                    regular one" reasoning applied to a different control: a
                    picker written twice is brief 59's finding waiting. */}
                <StageLinkField
                  form={form}
                  setForm={setForm}
                  cases={cases}
                  collectiveCases={collectiveCases}
                  typeEntries={actEntries as readonly TaxEntry[]}
                  stageTypeEntries={caseStageEntries as readonly TaxEntry[]}
                  jurisdictions={jurisdictions}
                  kind="task"
                  t={t}
                />
                {F("Title *", "title", "", 1)}
                <div className="field-row">
                  <div className="field">
                    <label>
                      {t("field.due_date", { fallback: "Due Date" })}
                    </label>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <input
                        className="field-input"
                        style={{ flex: 1, margin: 0 }}
                        type="date"
                        value={(form.dueDate || "").slice(0, 10)}
                        onChange={(e) => {
                          // A due date no longer writes a level. It used to
                          // set "medium" when a date was given and "low" when
                          // it was cleared, suppressed by a `_priManual` flag
                          // whose existence was the admission that the guess
                          // was often wrong. The ruling removes its premise:
                          // the ladder is the firm's, and for a firm whose
                          // levels are dominus litis / associate /
                          // administrative a deadline says nothing about which
                          // one an item sits at. It also silently DEMOTED —
                          // clearing a date dropped a chosen level to the
                          // bottom with no visible cause.
                          const v = e.target.value || null;
                          setForm((p: any) => {
                            if (p.id) return { ...p, dueDate: v };
                            const on = p.templatesOn !== false;
                            const tpl = on
                              ? _buildTplItems(actEntries, p.type, v)
                              : [];
                            return {
                              ...p,
                              dueDate: v,
                              subtasks: _reconcileTpl(p.subtasks, tpl, on),
                            };
                          });
                        }}
                      />
                      {form.dueDate && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{
                            padding: "4px 6px",
                            fontSize: FS.s10,
                            flexShrink: 0,
                          }}
                          title={t("modal.task.clear_due_date", {
                            fallback: "Clear due date",
                          })}
                          onClick={() =>
                            setForm((p: any) => {
                              if (p.id) return { ...p, dueDate: null };
                              const on = p.templatesOn !== false;
                              return {
                                ...p,
                                dueDate: null,
                                subtasks: _reconcileTpl(p.subtasks, [], on),
                              };
                            })
                          }
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Brief 86 §2e-5 — CASE MATRIX sits beside DUE DATE and
                      REMINDER beside LEVEL, so the row order matches the Event
                      modal's. The swap is the whole item: nothing about either
                      control changes here. */}
                  <div className="field">
                    <label>
                      {t("field.case_matrix", { fallback: "Case Matrix" })}
                    </label>
                    {/* Brief 86 §2e-6 — the app's normal field styling, at
                        half width like the others. It was an `inline-flex` box
                        holding a `btn btn-ghost` pill, so it took the width of
                        its own label text and sat at a different height from
                        every field beside it. Now: `field-input` on the
                        trigger, so it fills its grid cell and takes --field-h
                        from the same rule the selects do. The CLEAR affordance
                        stops being a second button that widened the row — it is
                        an × inside the control, the shape the Due Date field
                        two rows up already uses. Nothing about the picker's
                        behaviour moves; the popover still anchors on this
                        relative wrapper. */}
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        className="field-input"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          width: "100%",
                          textAlign: "left",
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                        onClick={() => setEisenPickerOpen((v: any) => !v)}
                      >
                        <EisenBadge
                          q={form.caseEisenhower || eisenAuto(form.type)}
                          size={14}
                        />
                        <span>
                          {eisenLabel(form.caseEisenhower || eisenAuto(form.type))}
                        </span>
                        {!form.caseEisenhower && (
                          <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                            {t("common.auto_paren", { fallback: "(auto)" })}
                          </span>
                        )}
                        {form.caseEisenhower && (
                          <span
                            role="button"
                            tabIndex={0}
                            title={t("common.clear", { fallback: "Clear" })}
                            style={{
                              marginLeft: "auto",
                              display: "inline-flex",
                              alignItems: "center",
                              color: "var(--tm)",
                            }}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              updateField("caseEisenhower", "");
                            }}
                            onKeyDown={(ev) => {
                              if (ev.key === "Enter" || ev.key === " ") {
                                ev.stopPropagation();
                                ev.preventDefault();
                                updateField("caseEisenhower", "");
                              }
                            }}
                          >
                            <X size={11} />
                          </span>
                        )}
                      </button>
                      {eisenPickerOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            marginTop: 4,
                            background: "var(--bc)",
                            border: "1px solid var(--br)",
                            borderRadius: RAD.r8,
                            padding: 6,
                            boxShadow: "0 6px 20px rgba(0,0,0,.6)",
                            zIndex: Z.inlineMenu,
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 4,
                            minWidth: 260,
                          }}
                        >
                          {EISEN_QUADRANTS.map((q) => (
                            <div
                              key={q.k}
                              style={{
                                padding: "8px 10px",
                                border: "1px solid " + q.border,
                                borderRadius: RAD.r6,
                                background: q.bg,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: FS.s10_5,
                                color: q.accent,
                                fontWeight: 600,
                              }}
                              onClick={() => {
                                updateField("caseEisenhower", q.k);
                                setEisenPickerOpen(false);
                              }}
                            >
                              <EisenBadge q={q.k} size={16} />
                              <span>{eisenLabel(q.k)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* BRIEF 87 §7 T1 — REMINDER | LEVEL, matching Event, and
                      one definition with it since step 9b's sibling: the two
                      fields are `ReminderLevelFields`. They stay at the tail of
                      THIS row (which opens with Due Date) rather than moving
                      into a row of their own — the component renders the two
                      fields and not their wrapper, precisely so neither grid
                      moves. */}
                  <ReminderLevelFields
                    S={S}
                    form={form}
                    updateField={updateField}
                    reminderOptsList={reminderOptsList}
                    itemLevelEntries={itemLevelEntries}
                    t={t}
                  />
                </div>
                {/* Brief 86 §2e-6/§2e-7 — ASSIGNED TO was a full-width row
                    holding a bare box (AssigneePicker defaults to className
                    "fi", the table-row input class), and STATUS sat in the
                    template-toggle's row. They pair here at half width, both on
                    field-input, which is what "the same grid discipline" means:
                    half or full, nothing between, one height. */}
                <div className="field-row">
                  <div className="field">
                    <label>
                      {t("field.assigned_to", { fallback: "Assigned To" })}
                    </label>
                    <AssigneePicker
                      className="field-input"
                      co={collaborators}
                      currentUser={currentUser}
                      value={form.assignedCoId}
                      // Phase 28 (item F) — edit-grant holders (admin or a
                      // caseView.tasks.edit grant on this case/cc) may assign
                      // to ANY collaborator, not just themselves.
                      canAssignAll={
                        !!_appIsAdmin ||
                        _appPerms.can("caseView.tasks.edit", {
                          caseId: form.caseId,
                          ccId: form.ccId,
                        })
                      }
                      onChange={(v) => {
                        const _v = v ? +v : null;
                        setForm((p: any) => ({
                          ...p,
                          assignedCoId: _v ?? "",
                          assigned: _v ? [_v] : [],
                        }));
                      }}
                    />
                  </div>
                  {(() => {
                    const _stOpts = (
                      taskStatusesList && taskStatusesList.length
                        ? taskStatusesList
                        : TASK_STATUSES
                    )
                      .map((o: any) => (Array.isArray(o) ? o : [o.v, o.l]));
                    /* BRIEF 136 §1 — C58's OTHER HALF, WITHDRAWN. This read:
                         C58 — no NEW way to set "waiting": the option is
                         offered only while the row already carries it, so
                         legacy rows keep rendering/saving their value.
                           .filter(([v]) => v !== "waiting"
                                            || form.status === "waiting")
                       C58 is overruled — the pill now offers every status
                       except `archived`, and `waiting` is one of them. A modal
                       that went on hiding the option would be the disagreement
                       C58 was written to prevent, pointing the other way: a
                       user could set `waiting` from the row and then not find
                       it in the form that edits the same field. */
                    /* Round 13 (C77a) — same affordance rule as the pill:
                       no case visibility (off-team temp assignee) → the
                       status renders read-only in the modal too. */
                    const _stVis =
                      (!form.caseId && !form.ccId) ||
                      cases.some((c: any) => c.id === +form.caseId) ||
                      collectiveCases.some((c: any) => c.id === +form.ccId);
                    if (_stVis) return S("Status", "status", _stOpts);
                    const _stCur = _stOpts.find(
                      ([v]: any) => v === (form.status || "todo"),
                    );
                    return (
                      <div className="field">
                        <label>{_fl("Status")}</label>
                        <div
                          className="field-input"
                          style={{
                            background: "var(--bi)",
                            color: "var(--ts)",
                            cursor: "default",
                            minHeight: "var(--field-h,30px)",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {_stCur
                            ? _stCur[1]
                            : form.status ||
                              t("app.status_todo", { fallback: "todo" })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <SubtaskChecklist {...({} as any)}
                  locked={false}
                  /* BRIEF 87 §7 (E3's twin) — the Insert/Template button
                     moves INSIDE the section, exactly as the Event modal's
                     does. Its whole `field-row` went with it: the row held
                     nothing but this button and a `<label>&nbsp;</label>`
                     spacer that existed only to align it to a field grid it
                     no longer sits in. */
                  headerSlot={
                    <TemplateSubtasksButton
                      kind="task"
                      form={form}
                      setForm={setForm}
                      entries={actEntries}
                      rows={tasks}
                      setRows={setTasks}
                      t={t}
                      toast={toast}
                    />
                  }
                  parentTaskId={form.id || null}
                  initialSubtasks={form.id ? undefined : form.subtasks || []}
                  controlledItems={form.id ? undefined : form.subtasks || []}
                  canWrite={true}
                  /* C59 — per-row assignee picker (delegation): same
                     edit-grant decision as the modal's own Assigned To
                     picker above. */
                  co={collaborators}
                  currentUser={currentUser}
                  assignable
                  canAssignAll={
                    !!_appIsAdmin ||
                    _appPerms.can("caseView.tasks.edit", {
                      caseId: form.caseId,
                      ccId: form.ccId,
                    })
                  }
                  onLocalChange={(arr: any) => updateField("subtasks", arr)}
                  onItemsChange={(arr: any) => {
                    if (form.id)
                      setTasks((prev: any) =>
                        prev.map((x: any) =>
                          x.id === form.id ? { ...x, subtasks: arr } : x,
                        ),
                      );
                  }}
                  /* BRIEF 91 §1a — THE TIMER COLUMN LEAVES THE MODALS, AND THAT
                     IS WHAT "one width" MEASURED TO.

                     The placeholder is one shared key, so what differed between
                     the two modals' subtask description field was the surrounding
                     columns — and the measurement is exactly this: THIS modal
                     passed `onLogTime`/`onStartTimer` and the Event modal passed
                     neither, so `_hasTimerCol` reserved two 26px slots and their
                     two 6px gaps here and nothing there. 64px, which is the
                     difference §1a asks to remove by taking the Event modal's —
                     the longer one — in both. There are exactly two ways to make
                     one width out of two column sets, and adding the column to
                     the Event modal produces the SHORTER one.

                     So the five timer props stop here. They do NOT stop
                     existing: the timer and the time-log watch live on the
                     DRAWERS, which is where §2 and §3 of this brief put them —
                     the task drawer (CaseTaskRow.tsx:1117), the compact column,
                     and the event drawer, which gains them in this same branch.
                     A modal is the form for the list; the drawer is the surface
                     you work on. Ticking a subtask done here still offers the
                     time log (`onCheckDone`, just below), so no log path is
                     lost from this screen.

                     MEASURED, at the 480px modal: the add row's description
                     field goes 256−D → 320−D px in this modal and 314−D → 320−D
                     in the Event modal (D = the date column), so both are now
                     the Event modal's width and both got longer. */
                  onCheckDone={(s: any) => {
                    /* Round 15 (C91) — funnel through askSubtaskDone so the
                       prompt carries its deterministic te payload (the old
                       direct setSubtaskDoneAsk left the prompt to read
                       ambient state, with a string caseId in the fallback). */
                    const tk2 = tasks.find((x: any) => x.id === form.id);
                    askSubtaskDone(
                      tk2 || {
                        id: form.id,
                        title: form.title || "",
                        caseId: form.caseId ? +form.caseId : null,
                        ccId: form.ccId ? +form.ccId : null,
                        type: form.type,
                      },
                      s,
                    );
                  }}
                  isAdmin={true}
                />
                {/* Round 13 (C72) — DOCUMENT LIST section (same component
                    family as the checklist) replaces the single docLink
                    field; existing docLink values were carried over as the
                    first list entry by the migration backfill. The OneDrive
                    browse remains as an add path: picking APPENDS a
                    document row directly. */}
                <TaskDocsPane
                  taskId={form.id || null}
                  locked={false}
                  initialDocs={
                    form.id
                      ? (() => {
                          const _tk2 = tasks.find((x: any) => x.id === form.id);
                          return _tk2 && Array.isArray(_tk2.docs)
                            ? _tk2.docs
                            : undefined;
                        })()
                      : form.docs || []
                  }
                  canWrite={true}
                  onLocalChange={(arr) => updateField("docs", arr)}
                  onDocsChange={(arr) => {
                    if (form.id)
                      setTasks((prev: any) =>
                        prev.map((x: any) =>
                          x.id === form.id ? { ...x, docs: arr } : x,
                        ),
                      );
                  }}
                  /* 110 §1a — the matter binding, derived exactly as the
                     retired OneDriveDocButton derived it (collapse the form's
                     four matter aliases, disambiguate by list membership);
                     the pane owns the button and the write now — a pick fills
                     the dialog's draft and Add/Save persists through the
                     pane's own paths, saved and unsaved alike. A matterless
                     or unresolvable matter keeps the old unconfined browse
                     ({}), which is what the closure produced there too. */
                  oneDrive={(() => {
                    const _cid =
                      +(form.caseId || form._ci || form.caId || form.ccId || 0) ||
                      null;
                    if (_cid && cases.some((c: any) => c.id === _cid))
                      return { caseId: _cid };
                    if (_cid && collectiveCases.some((c: any) => c.id === _cid))
                      return { ccId: _cid };
                    return {};
                  })()}
                  /* 151C §6 — the doc dialog's Link to Stage stops being a
                     second view of the form's stageId (that pen is retired
                     from this flow; StageLinkField above keeps the
                     belongs-to fact) and becomes the PURPLE itemLink,
                     reconciled by the pane against the saved row. EDIT MODE
                     ONLY: an unsaved task has no id and the links store has
                     nothing to link — the select is absent until the task
                     exists, and the task's own StageLinkField is on screen
                     in this same modal meanwhile. */
                  stageLink={(() => {
                    const _tid = Number(form.id || 0);
                    if (!_tid) return undefined;
                    const _cid =
                      +(form.caseId || form._ci || form.caId || form.ccId || 0) ||
                      null;
                    const _m = _cid
                      ? cases.find((c: any) => c.id === _cid) ||
                        collectiveCases.find((c: any) => c.id === _cid)
                      : null;
                    if (!_m || !Array.isArray(_m.stages) || !_m.stages.length)
                      return undefined;
                    return {
                      stages: _m.stages,
                      matterKind: cases.some((c: any) => c.id === _cid)
                        ? ("ca" as const)
                        : ("cc" as const),
                      matterId: _m.id,
                      matterRow: _m,
                    };
                  })()}
                />
                {/* BRIEF 126 §9 — 125's `OpenEmailButton` STOOD HERE AND IS
                    RETIRED. [RULED] "the email link should just be added as a
                    document, triggering an icon and as such appear when the
                    drawer is opened", and the fence: "§9 retires the button; it
                    does not keep both."

                    The affordance is the documents pane ONE COMPONENT ABOVE.
                    125 built this control for the case where Outlook gave no
                    `webLink` and `_dropDocs` therefore omitted the row; §9 makes
                    the row unconditional — carrying `message:<Message-ID>` when
                    no link is known — so there is nothing left for a second
                    control to cover. `Task.emailGraphId` is untouched and still
                    the provenance column; it is simply no longer an affordance.
                    Every refusal this button said now lives on the document
                    row's own open path (`lib/emailLink.ts`). */}
{T("Notes", "notes")}
              </>
          </ModalLayout>
  );
}
